/**
 * Toasts.
 *
 * Confirmations are transient rather than blocking: a merchant marking an order
 * ready must not have to dismiss a dialog before handling the next one. The
 * surface renders into the `PortalHost` the app root mounts once, so a toast is
 * never clipped by the scroll view that triggered it.
 *
 * There are two slots and no more: one plain message, one carrying an action. A
 * screen that fires in a rush — the board, where a hand-over is scan → tap →
 * toast — used to append a bar per message and pile them over the cards a counter
 * is working. Each bar repeats what the card behind it already says. Measured on
 * the running board a bar is 48 points tall, so four of them is 216 points: a
 * third of the board's usable height, covering the work.
 *
 * A plain message replaces the plain slot. Consecutive messages that name the
 * same {@link ToastOptions.key} collapse into a count instead, so a rush reads
 * `Order #4 — Collected`, then `2 orders — Collected`, then `3 orders — Collected`
 * — which is more legible than three bars, not merely quieter.
 *
 * The action slot is separate rather than competing. An undo is the one message
 * that cannot be recovered by looking at the screen, and a refusal must not be
 * dropped because an undo happens to be up; a slot each means neither can lose to
 * the other, and the board — which sends no action toasts — still shows one bar.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Pressable, View } from 'react-native';

import { Portal } from '@rn-primitives/portal';

import { Text } from '../components/ui/text';
import { Row } from '../components/layout';
import { cn } from '../lib/utils';
import type { Tone } from '../lib/theme';

/** What one `show` call may say about the message. */
export interface ToastOptions {
  tone?: Tone;
  /**
   * Groups repeats of one message.
   *
   * Two plain messages shown with the same key collapse into one bar and a count.
   * A message with no key replaces the slot without counting, and a message
   * carrying an action is never grouped.
   */
  key?: string;
  /**
   * What a run longer than one says, given its length.
   *
   * A function rather than a template so the caller can put it through the
   * dictionary: `(count) => t('…', { count, status })`. Without it a run reads
   * `{message} ×{count}`.
   */
  run?: (count: number) => string;
  actionLabel?: string;
  onAction?: () => void;
}

interface ToastMessage {
  id: number;
  message: string;
  tone: Tone;
  key?: string;
  /** How many messages this bar stands for; 1 unless a run collapsed. */
  count: number;
  run?: (count: number) => string;
  actionLabel?: string;
  onAction?: () => void;
}

interface ToastContextValue {
  show: (message: string, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue>({ show: () => undefined });

/** Portal key for the toast surface; one provider per app owns it. */
const PORTAL_NAME = 'yummy-toasts';

/** Dismissal delays: a message with an action stays up long enough to be used. */
const TOAST_MS = 4000;
const ACTION_TOAST_MS = 8000;

/** The solid tone rule down a toast's leading edge. */
const TOAST_RULE: Record<Tone, string> = {
  neutral: 'border-l-subtle-foreground',
  brand: 'border-l-primary',
  success: 'border-l-success',
  info: 'border-l-info',
  progress: 'border-l-progress',
  warning: 'border-l-warning',
  danger: 'border-l-destructive',
};

/**
 * Reads the toast API.
 *
 * @returns A `show` function for transient confirmations.
 */
export function useToast(): ToastContextValue {
  return useContext(ToastContext);
}

/**
 * Renders toast messages above the app.
 *
 * @param props Children.
 * @returns The children, plus the portal that shows the messages.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [plain, setPlain] = useState<ToastMessage | null>(null);
  const [action, setAction] = useState<ToastMessage | null>(null);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  // Mirrors of the two slots, read by `show` to decide whether an arriving
  // message continues a run. Scheduling a dismissal is a side effect, so it
  // cannot happen inside a state updater: React may run one twice, and a run that
  // scheduled two timers would dismiss a bar that had just been refreshed.
  const plainRef = useRef<ToastMessage | null>(null);
  const actionRef = useRef<ToastMessage | null>(null);

  const clearTimer = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const dismiss = useCallback(
    (id: number) => {
      clearTimer(id);
      if (plainRef.current?.id === id) {
        plainRef.current = null;
        setPlain(null);
      }
      if (actionRef.current?.id === id) {
        actionRef.current = null;
        setAction(null);
      }
    },
    [clearTimer]
  );

  /** Restarts a slot's clock, so a run stays up for its full window after the last event. */
  const schedule = useCallback(
    (id: number, ms: number) => {
      clearTimer(id);
      timers.current.set(
        id,
        setTimeout(() => dismiss(id), ms)
      );
    },
    [clearTimer, dismiss]
  );

  // A timer that outlives the provider would set state after unmount.
  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending.values()) clearTimeout(timer);
      pending.clear();
    };
  }, []);

  const show = useCallback<ToastContextValue['show']>(
    (message, options) => {
      const base = {
        message,
        tone: options?.tone ?? 'neutral',
        ...(options?.key ? { key: options.key } : {}),
        ...(options?.run ? { run: options.run } : {}),
        ...(options?.actionLabel ? { actionLabel: options.actionLabel } : {}),
        ...(options?.onAction ? { onAction: options.onAction } : {}),
      };

      if (base.actionLabel) {
        const id = nextId.current++;
        const next: ToastMessage = { id, count: 1, ...base };
        actionRef.current = next;
        setAction(next);
        schedule(id, ACTION_TOAST_MS);
        return;
      }

      const current = plainRef.current;
      if (current && base.key && current.key === base.key) {
        // The same run: keep the identity, so the bar does not remount under the
        // merchant's eyes, and count what it now stands for.
        const next: ToastMessage = { ...current, ...base, count: current.count + 1 };
        plainRef.current = next;
        setPlain(next);
        schedule(next.id, TOAST_MS);
        return;
      }

      const id = nextId.current++;
      const next: ToastMessage = { id, count: 1, ...base };
      plainRef.current = next;
      setPlain(next);
      schedule(id, TOAST_MS);
    },
    [schedule]
  );

  const value = useMemo(() => ({ show }), [show]);

  // The action bar leads, so the undo holds one position rather than moving with
  // whatever was confirmed after it.
  const messages = [...(action ? [action] : []), ...(plain ? [plain] : [])];

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Portal name={PORTAL_NAME}>
        <View
          className="absolute right-0 bottom-0 left-0 items-center gap-2 p-4"
          pointerEvents="box-none">
          {messages.map((message) => (
            <View
              key={message.id}
              className={cn(
                'bg-foreground w-full max-w-[520px] rounded-md border-l-4 px-4 py-3 shadow-lg',
                TOAST_RULE[message.tone]
              )}
              accessibilityLiveRegion="polite">
              <Row spread gap="md" align="center">
                <Text className="text-primary-foreground flex-1 text-base">
                  {message.count > 1
                    ? (message.run?.(message.count) ?? `${message.message} ×${message.count}`)
                    : message.message}
                </Text>
                {message.actionLabel && message.onAction ? (
                  <Pressable
                    role="button"
                    onPress={() => {
                      // An action can be taken once. Leaving the toast up invites
                      // a second press of something that has already happened —
                      // and for something like undoing a move, a second press
                      // drags back items that may have been moved on since.
                      dismiss(message.id);
                      message.onAction?.();
                    }}
                    className="active:opacity-70">
                    <Text className="text-primary text-[13px] font-bold">
                      {message.actionLabel}
                    </Text>
                  </Pressable>
                ) : null}
              </Row>
            </View>
          ))}
        </View>
      </Portal>
    </ToastContext.Provider>
  );
}
